import { createSlice } from "@reduxjs/toolkit";
import {
  COMPLETED,
  ONBOARDING_COMPLETED,
  ONBOARDING_NOT_STARTED,
  ONBOARDING_FAILED,
  MANUAL_JOB_STARTED,
  ONBOARDING_STARTED,
} from "../config/CONSTANTS";

import moment from "moment";

const jobstatusSlice = createSlice({
  name: "jobstatus",
  initialState: {
    sync7DaysStatus: false, //set to true when 7 days sync job is complete
    sync60DaysStatus: false, //set to true when 60 days sync job is complete
    onboardingSyncJobStatus: null, //set to true when onboarding sync job 7 and 60 days is complete
    ongoingJobStatus: false, // set to true if sync job is running [manual or onboarding job is running]
    lastJobSyncTimestamp: null, // set to last successful time stamp
    historicalJobStatusObject: {}, //the current status of historical job data download
    historicaldownloadedDataSince: null, // historical data since available date.
    jobProgressPercentage: 0, // progress percentage of historical data.
    incomingInventoryStatus7days: null, //set to true when incoming inventory for 7 days is completed
    incomingInventoryStatus60days: null, //set to true when incoming inventory for 60 days is completed
    isOnboardingCompleted: false, //set to true when onboarding is completed
    // Add new fields to store raw status data
    onboardingStatusRaw: null, // stores forecast.onboarding_status (COMPLETED, STARTED, etc.)
    sync7DaysStatusRaw: null, // stores syncOrderState, syncInventoryState
    sync60DaysStatusRaw: null, // stores asyncOrderState, asyncInventoryState

    // Location switch loader state.
    // Kept fully separate from ongoingJobStatus/onboarding so the location-switch
    // loader never interferes with the onboarding or incoming-inventory bars.
    locationSwitchInProgress: false, // set to true while a MODE_LOCATION_SWITCH bulk job runs
    locationSwitchName: null, // name of the location being switched to (for the loader message)
    // Guard: we only hide the loader once we have actually SEEN a pending bulk op.
    // Prevents a premature hide during the brief gap before the switch bulk-op row exists.
    locationSwitchSawPending: false,
    // Set true when a switch ends in FAILED so the Header can surface an error toast.
    locationSwitchFailed: false,
    // Name of the location captured at the moment of failure (locationSwitchName is
    // nulled out in the same terminal transition, so we snapshot it here for the toast).
    locationSwitchFailedName: null,
  },
  reducers: {
    set7daysjobStatus(state, action) {
      const data = action.payload.data;
      
      // Store raw status data
      state.sync7DaysStatusRaw = {
        syncOrderState: data.syncOrderState,
        syncInventoryState: data.syncInventoryState,
        onboardingStatus: data.onboardingStatus, // This is forecast.onboarding_status
      };
      
      if(data.syncIncomingInventoryState === COMPLETED ){
        state.incomingInventoryStatus7days = true
      }
      else{
        state.incomingInventoryStatus7days = true
      }
      if (
        data.syncInventoryState === ONBOARDING_COMPLETED &&
        data.syncOrderState === ONBOARDING_COMPLETED
      ) {
        state.sync7DaysStatus = true;
      } else {
        state.sync7DaysStatus = false;
      }
      
      // Check if onboarding is completed
      if(data.onboardingStatus === COMPLETED){
        state.isOnboardingCompleted = true;
        state.onboardingSyncJobStatus = true;
        // When onboarding is completed, allow sync button
        state.ongoingJobStatus = false;
      } else if(data.onboardingStatus === ONBOARDING_STARTED) {
        // When onboarding is STARTED, ensure progress bar shows and disable sync button
        state.onboardingSyncJobStatus = false;
        state.isOnboardingCompleted = false;
        state.ongoingJobStatus = true; // Disable Sync Inventory button during onboarding
      }
    },
    setHistoricaldownloadedDataSince(state, action) {
      const data = action.payload.data;
      state.historicaldownloadedDataSince = data;
    },
    setHistoricaljobStatus(state, action) {
      const data = action.payload.data;
      const status = action.payload.status;
      const asyncInventoryState = action.payload.asyncInventoryState;
      const asyncOrderState = action.payload.asyncOrderState;

      //if failed job the stop
      if (action.payload.data === null) {
        state.onboardingSyncJobStatus = false;
        window.localStorage.setItem("onboardingJobStatusCheck", false);
        state.historicalJobStatusObject = {
          status: false,
          response: null,
        };

        return;
      }

      //60 days asyncInventoryState is in progress
      if (status === true) {
        state.onboardingSyncJobStatus = true;
        state.jobProgressPercentage = 1;
        window.localStorage.setItem("onboardingJobStatusCheck", true);
        
        // Check if both async states are STARTED (60-day sync in progress)
        if (asyncInventoryState === ONBOARDING_STARTED && asyncOrderState === ONBOARDING_STARTED) {
          state.historicalJobStatusObject = {
            status: true,
            message: "We are pulling orders older than 60 days. Historical orders will be imported next.",
          };
        } else {
          state.historicalJobStatusObject = {
            status: true,
            message:
              "We are now pulling orders older than 7 days and rest of the products.",
          };
        }
      } else {
        if (
          data.data.startDate === null &&
          data.data.endDate === null &&
          data.data.downloadedDataSince === null
        ) {
          state.onboardingSyncJobStatus = false;
          state.jobProgressPercentage = 1;
          state.historicaldownloadedDataSince = data.data.ordersAvailableSince;
          window.localStorage.setItem("onboardingJobStatusCheck", true);
          state.historicalJobStatusObject = {
            status: false,
            response: data,
          };
        } else {
          const startDate = moment(data.data.startDate);
          const endDate = moment(data.data.endDate);
          const downloadedDataSince = moment(data.data.downloadedDataSince);
          const totalDays = startDate.diff(endDate, "days");
          const daysDownloaded = startDate.diff(downloadedDataSince, "days");
          const progressPercentage = Math.min(
            ((daysDownloaded / totalDays) * 100).toFixed(2),
            100
          );
          const today = new Date(data.data.downloadedDataSince);
          const formattedDate = moment(today).format("MMMM Do YYYY");
          state.historicaldownloadedDataSince = data.data.downloadedDataSince;
          state.onboardingSyncJobStatus = true;
          window.localStorage.setItem("onboardingJobStatusCheck", true);
          state.jobProgressPercentage = progressPercentage;
          state.historicalJobStatusObject = {
            status: true,
            message: `Imported orders upto ${formattedDate}, Loading older orders. Manually Refresh to view latest.`,
          };
        }
      }
    },
    set60daysjobStatus(state, action) {
      const data = action.payload.data;
      
      // Store raw status data
      state.sync60DaysStatusRaw = {
        asyncOrderState: data.asyncOrderState,
        asyncInventoryState: data.asyncInventoryState,
        onboardingStatus: data.onboardingStatus, // This is forecast.onboarding_status
      };
      
      if(data.onboardingStatus === COMPLETED){
        state.isOnboardingCompleted = true;
      }
      if(data.asyncInventoryState === COMPLETED){
        state.incomingInventoryStatus60days = true;
      }
      else{
        state.incomingInventoryStatus60days = false;
      }
      if (
        (data.asyncInventoryState === ONBOARDING_COMPLETED &&
          data.asyncOrderState === ONBOARDING_COMPLETED) ||
        (data.asyncInventoryState === ONBOARDING_NOT_STARTED &&
          data.asyncOrderState === ONBOARDING_NOT_STARTED) ||
        (data.asyncInventoryState === ONBOARDING_FAILED &&
          data.asyncOrderState === ONBOARDING_FAILED)
      ) {
        state.sync60DaysStatus = true;
        state.onboardingSyncJobStatus = true;
        window.localStorage.setItem("onboardingJobStatusCheck", true);
      } else {
        state.sync60DaysStatus = false;
        state.onboardingSyncJobStatus = false;
        window.localStorage.setItem("onboardingJobStatusCheck", false);
      }
    },
    setOnboardingJobStatus(state, action) {
      const data = action.payload.data;
      if (data.state === COMPLETED) {
        state.onboardingSyncJobStatus = true;
      } else {
        state.onboardingSyncJobStatus = false;
      }
    },
    // Frontend-derived "onboarding is effectively complete" promotion.
    //
    // Background: the backend's `forecast.onboarding_status` is known to stay stuck on
    // "PROCESSING" (and `asyncOrderState` on "STARTED") for merchants with historical
    // orders authorized, even after the historical job has fully completed. Because of
    // that, set7daysjobStatus / set60daysjobStatus never see "COMPLETED" and the
    // `isOnboardingCompleted` flag never flips — which means the one-time
    // "You are all set now" normalization card from the In-App Review Flow can never
    // render for those merchants.
    //
    // This reducer lets Forecasting.jsx mark onboarding as complete once it has
    // observed a reliable client-side "done" signal (historical job transitioned to
    // status:false, or 60-day sync reached COMPLETED for non-historical users).
    // Idempotent — only ever flips the flag to true; never back to false.
    markOnboardingCompleted(state) {
      state.isOnboardingCompleted = true;
      state.onboardingSyncJobStatus = true;
    },
    setLastSyncTimestamp(state, action) {
      const data = action.payload.data[action.payload.data.length - 1];
      state.lastJobSyncTimestamp = data.lastUpdated;
      window.localStorage.setItem("inventoryJobStatusDate", data.lastUpdated);
    },
    setManualSyncJobStatus(state, action) {
      const data = action.payload.data;

      if (data.status === MANUAL_JOB_STARTED) {
        state.ongoingJobStatus = true;
        window.localStorage.setItem("manualJobStatusCheck", true);
      } else {
        state.ongoingJobStatus = false;
        window.localStorage.setItem("manualJobStatusCheck", false);
      }
    },
    setOngoingSyncJobStatus(state, action) {
      const data = action.payload.data;

      if (data.pageRecordCount > 0) {
        state.ongoingJobStatus = true;
        window.localStorage.setItem("manualJobStatusCheck", true);
      } else {
        // While a location switch is settling, the dedicated regen status (not the
        // pending bulk-op count) is the source of truth. A stray pending=0 poll
        // (e.g. from initForecastingMain) must NOT clear ongoingJobStatus early, or
        // the report would refetch before the new location's rows exist. The
        // true->false flip is driven by setLocationSwitchRegenStatus instead.
        if (state.locationSwitchInProgress === true) {
          return;
        }
        state.ongoingJobStatus = false;
        window.localStorage.setItem("manualJobStatusCheck", false);
      }
    },

    /*
     * Begins the location-switch loader.
     * Called from the Settings location card right after a real location is saved
     * (the backend kicks off a MODE_LOCATION_SWITCH bulk job for real locations).
     * Resets the "saw pending" guard so the very next poll can start tracking.
     */
    startLocationSwitch(state, action) {
      state.locationSwitchInProgress = true;
      state.locationSwitchName = action.payload?.name || null;
      state.locationSwitchSawPending = false;
      // A new switch clears any leftover failure banner from a previous attempt.
      state.locationSwitchFailed = false;
      state.locationSwitchFailedName = null;
    },

    /*
     * Applies the dedicated backend location-switch regen status.
     *
     * This is now the single source of truth for ending a switch (replacing the old
     * pending-bulk-op guessing). The backend marks the property IN_PROGRESS the moment
     * the switch is saved and flips it to SUCCESS/FAILED only once the forecast rows
     * have actually finished regenerating.
     *
     *   IN_PROGRESS       -> keep loaders on (ongoingJobStatus stays true).
     *   SUCCESS / FAILED  -> terminal: clear the loader and flip ongoingJobStatus
     *                        true->false, which drives the report refetch.
     *
     * Example payload: { state: "SUCCESS", inProgress: false, locationId: "123" }
     */
    setLocationSwitchRegenStatus(state, action) {
      const data = action.payload?.data || {};
      const inProgress = data.inProgress === true;

      if (inProgress) {
        // Still regenerating: keep everything visible.
        state.locationSwitchInProgress = true;
        state.ongoingJobStatus = true;
      } else if (state.locationSwitchInProgress) {
        // Terminal (SUCCESS/FAILED) while we were tracking a switch: clear it.
        // The ongoingJobStatus true->false flip is what triggers the refetch.
        // On FAILED, snapshot the name (before nulling it) and raise the error flag
        // so the Header can show a dismissible error toast.
        if (data.state === "FAILED") {
          state.locationSwitchFailed = true;
          state.locationSwitchFailedName = state.locationSwitchName;
        }
        state.locationSwitchInProgress = false;
        state.locationSwitchName = null;
        state.locationSwitchSawPending = false;
        state.ongoingJobStatus = false;
        window.localStorage.setItem("manualJobStatusCheck", false);
      }
    },

    /*
     * Clears the location-switch failure banner (used by the toast's onDismiss).
     */
    dismissLocationSwitchFailure(state) {
      state.locationSwitchFailed = false;
      state.locationSwitchFailedName = null;
    },

    /*
     * Force-clears the location-switch loader.
     * Used by the Header's max-timeout fallback so the loader can never hang forever
     * if the switch job is deferred/retried (it can retry up to 20x over ~3min when
     * another bulk op is already running). Also clears ongoingJobStatus so the report
     * refetch effect fires and the Sync button re-enables.
     */
    endLocationSwitch(state) {
      state.locationSwitchInProgress = false;
      state.locationSwitchName = null;
      state.locationSwitchSawPending = false;
      state.ongoingJobStatus = false;
      window.localStorage.setItem("manualJobStatusCheck", false);
    },
  },
});

export const jobstatusActions = jobstatusSlice.actions;

export default jobstatusSlice;
